import { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Database from "utils/database.utils";
import {
    CompanySummary,
    MeetingNotesCategory,
    MeetingNotesSummary,
    SentimentMovingAveragePoint,
} from "./types";
import { getLatestMeetingNote } from "./utils";
import { ALL_CATEGORIES_VALUE } from "./components/MeetingHistorySection";

export const useSelectCompany = () => {
    const history = useHistory();
    const params = useParams<{ company_id?: string }>();
    const [companyId, setCompanyId] = useState<string>("");

    useEffect(() => {
        if (!params.company_id) {
            return;
        }
        if (companyId !== params.company_id) {
            setCompanyId(params.company_id);
        }
    }, [params.company_id, companyId]);

    function onCompanyChange(companyId: string) {
        const newUrl = `/company/company-summary/${companyId}`;
        history.push(newUrl);
    }

    return { id: companyId, onChange: onCompanyChange };
};

export const useCompanySummary = (companyId: string) => {
    const [summary, setSummary] = useState<CompanySummary | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (companyId) {
            (() => {
                setLoading(true);
                Database.get(`api/bqmq_gen_ai/ai-summary/${companyId}`)
                    .then(async (response) => {
                        if (response.ok) {
                            const data = await response.json();
                            setSummary(data.data);
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            })();
        } else {
            setLoading(false);
        }
    }, [companyId]);

    return { data: summary, loading };
};

export const useMeetingNotesCategories = () => {
    const [categories, setCategories] = useState<MeetingNotesCategory[]>([]);
    const [selected, setSelected] = useState<string>(ALL_CATEGORIES_VALUE);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const response = await Database.get(
                    "api/bqmq_gen_ai/ai-summary/meeting-notes-categories"
                );
                if (response.ok) {
                    const data = await response.json();
                    setCategories(data.data || []);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return {
        data: categories,
        selected,
        loading,
        onCategoryChange: setSelected,
    };
};

export const useSentimentMovingAverage = (
    companyId: string,
    selectedCategoryName: string,
    categories: MeetingNotesCategory[],
    meetingNotes: MeetingNotesSummary[]
): { data: SentimentMovingAveragePoint[]; loading: boolean } => {
    const [data, setData] = useState<SentimentMovingAveragePoint[]>([]);
    const [loading, setLoading] = useState(false);

    const categoryId =
        selectedCategoryName === ALL_CATEGORIES_VALUE
            ? null
            : categories.find((c) => c.name === selectedCategoryName)?.id ??
              null;

    useEffect(() => {
        if (!companyId || meetingNotes.length === 0) {
            setData([]);
            return;
        }

        setLoading(true);
        const params = new URLSearchParams();
        if (categoryId) params.set("category_id", categoryId);
        Database.get(
            `api/bqmq_gen_ai/ai-summary/${companyId}/sentiment-moving-average?${params.toString()}`
        )
            .then(async (response) => {
                if (response.ok) {
                    const json = await response.json();
                    setData(json.data ?? []);
                } else {
                    setData([]);
                }
            })
            .catch(() => setData([]))
            .finally(() => setLoading(false));
    }, [companyId, categoryId, meetingNotes]);

    return { data, loading };
};

export const useSelectedNote = (
    selectedCategory: string,
    meetingNotes: MeetingNotesSummary[]
) => {
    const [selectedNote, setSelectedNote] =
        useState<MeetingNotesSummary | null>(null);

    // Reset selected note when category changes
    useEffect(() => {
        setSelectedNote(null);
    }, [selectedCategory]);

    const handleMarkerClick = useCallback(
        (point: any) => {
            const clickedNote = meetingNotes.find(
                (note) =>
                    new Date(note.meeting_note_date).getTime() === point.x &&
                    note.sentiment_score === point.y &&
                    note.meeting_note_title === point.title
            );
            if (clickedNote) {
                setSelectedNote(clickedNote);
            }
        },
        [meetingNotes]
    );

    const latestMeetingNote = useMemo(
        () => getLatestMeetingNote(selectedCategory, meetingNotes),
        [selectedCategory, meetingNotes]
    );

    const displayNote = selectedNote || latestMeetingNote;

    return {
        selectedNote,
        setSelectedNote,
        handleMarkerClick,
        displayNote,
    };
};
